/**
 * Generic member-facing CRUD list + form for the four Carer Essentials
 * sections (documents, cpd, references, work-records). Field shape comes
 * from data/carerEssentialsSections.js — this component just renders,
 * validates presence of required fields, and talks to
 * /api/carer-essentials/{section}.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Paperclip, Download, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import PccUpgradePromo from "./PccUpgradePromo";

function emptyFormState(fields) {
  const state = {};
  for (const f of fields) state[f.name] = f.defaultValue || "";
  return state;
}

export default function CrudSection({ config, contextualPromo }) {
  const [items, setItems] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyFormState(config.fields));
  const [saving, setSaving] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/api/carer-essentials/${config.apiSection}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load records");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, [config.apiSection]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyFormState(config.fields));
    setPendingFile(null);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    const next = emptyFormState(config.fields);
    for (const f of config.fields) next[f.name] = item[f.name] ?? next[f.name];
    setForm(next);
    setPendingFile(null);
    setDialogOpen(true);
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    const missing = config.fields.filter((f) => f.required && !String(form[f.name] || "").trim());
    if (missing.length) {
      toast.error(`Please fill in: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.cpd_hours === "") payload.cpd_hours = null;
      let record;
      if (editingId) {
        const { data } = await api.patch(`/api/carer-essentials/${config.apiSection}/${editingId}`, payload);
        record = data;
      } else {
        const { data } = await api.post(`/api/carer-essentials/${config.apiSection}`, payload);
        record = data;
      }
      if (pendingFile) {
        const fd = new FormData();
        fd.append("file", pendingFile);
        await api.post(`/api/carer-essentials/${config.apiSection}/${record.id}/file`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(editingId ? `${config.itemLabel} updated` : `${config.itemLabel} added`);
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save — please try again");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/api/carer-essentials/${config.apiSection}/${id}`);
      toast.success(`${config.itemLabel} deleted`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (item) => {
    try {
      const res = await api.get(`/api/carer-essentials/${config.apiSection}/${item.id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.original_filename || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
  };

  return (
    <div data-testid={`ce-section-${config.apiSection}`}>
      <div className="container-jcc py-8 md:py-12 max-w-3xl">
        <Link to="/workspace/carer-essentials" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="ce-back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Carer Essentials
        </Link>

        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold">{config.title}</h1>
            <p className="mt-1.5 text-ink-muted max-w-xl">{config.description}</p>
          </div>
          <Button onClick={openCreate} data-testid="ce-add-record-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add {config.itemLabel}
          </Button>
        </div>

        {config.privacyNote && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ce-privacy-note">
            {config.privacyNote}
          </div>
        )}

        <div className="mt-6 space-y-3">
          {items === null && <p className="text-ink-muted">Loading…</p>}
          {items !== null && items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-ink-muted" data-testid="ce-empty-state">
              Nothing here yet — add your first {config.itemLabel} to get started.
            </div>
          )}
          {(items || []).map((item) => (
            <div key={item.id} className="card-base p-5 flex items-start justify-between gap-4" data-testid={`ce-record-${item.id}`}>
              <div className="min-w-0">
                <h3 className="font-display font-semibold" data-testid={`ce-record-title-${item.id}`}>{item[config.titleField]}</h3>
                <p className="mt-1 text-sm text-ink-muted">
                  {config.listMeta(item).join(" · ") || "\u00A0"}
                </p>
                {item.storage_path && (
                  <button
                    onClick={() => handleDownload(item)}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm text-teal font-medium hover:underline"
                    data-testid={`ce-download-${item.id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> {item.original_filename || "Download file"}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="icon" variant="outline" onClick={() => openEdit(item)} data-testid={`ce-edit-${item.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => handleDelete(item.id)}
                  disabled={deletingId === item.id}
                  data-testid={`ce-delete-${item.id}`}
                >
                  {deletingId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {contextualPromo && <div className="mt-8"><PccUpgradePromo variant="contextual" text={contextualPromo} /></div>}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="ce-record-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? `Edit ${config.itemLabel}` : `Add ${config.itemLabel}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {config.fields.map((f) => (
              <div key={f.name}>
                <Label htmlFor={`ce-field-${f.name}`}>{f.label}{f.required && " *"}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.name] || f.defaultValue} onValueChange={(v) => setField(f.name, v)}>
                    <SelectTrigger id={`ce-field-${f.name}`} data-testid={`ce-field-${f.name}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea
                    id={`ce-field-${f.name}`}
                    data-testid={`ce-field-${f.name}`}
                    value={form[f.name]}
                    onChange={(e) => setField(f.name, e.target.value)}
                    placeholder={f.placeholder}
                    rows={3}
                  />
                ) : (
                  <Input
                    id={`ce-field-${f.name}`}
                    data-testid={`ce-field-${f.name}`}
                    type={f.type}
                    step={f.step}
                    value={form[f.name]}
                    onChange={(e) => setField(f.name, e.target.value)}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}

            {config.allowFile && (
              <div>
                <Label htmlFor="ce-field-file">Attach file (PDF, Word doc or image, 15MB max)</Label>
                <div className="flex items-center gap-2">
                  <Input id="ce-field-file" data-testid="ce-field-file" type="file" onChange={(e) => setPendingFile(e.target.files?.[0] || null)} />
                  {pendingFile && (
                    <button type="button" onClick={() => setPendingFile(null)} className="text-ink-muted hover:text-ink" data-testid="ce-clear-file">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {pendingFile && (
                  <p className="mt-1 text-xs text-ink-muted flex items-center gap-1"><Paperclip className="w-3 h-3" /> {pendingFile.name}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="ce-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="ce-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              {editingId ? "Save changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
